import { DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR } from "@/constants";
import { REWARDS_WIDGET_NAMES } from "@/constants/widget-names";
import {
    growaveConfigurationToken,
    localizationInfoProviderToken,
    modalManagerToken,
} from "@/tokens";
import { isShadowRoot } from "@/utils/isShadowRoot/isShadowRoot";
import { TRANSLATION_NAMESPACES } from "@apps/rewards/constants/constants";
import RewardsAmountFormatter from "@apps/rewards/lib/RewardsAmountFormatter/RewardsAmountFormatter";
import RewardsCurrencyFormatter from "@apps/rewards/lib/RewardsCurrencyFormatter";
import RewardsBoostCampaignQuery from "@apps/rewards/modules/BoostCampaign/query/RewardsBoostCampaignQuery";
import RewardsBoostCampaignService from "@apps/rewards/modules/BoostCampaign/service/RewardsBoostCampaignService";
import RewardsEarningRulesService from "@apps/rewards/modules/EarningRules/service/RewardsEarningRulesService";
import RewardsProgramAccountQuery from "@apps/rewards/modules/ProgramAccount/query/RewardsProgramAccountQuery";
import RewardsProgramAccountService from "@apps/rewards/modules/ProgramAccount/service/RewardsProgramAccountService";
import RewardsStorefrontValuesService from "@apps/rewards/modules/StorefrontValues/service/RewardsStorefrontValuesService";
import RewardsDiResolverService from "@apps/rewards/services/RewardsDiResolverService/RewardsDIResolverService";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { AuthenticationModule } from "@modules/authentication/AuthenticationModule";
import { CommonModule } from "@modules/common_module/CommonModule";
import CustomerQuery from "@modules/customer_module/queries/CustomerQuery";
import CustomerService from "@modules/customer_module/services/CustomerService";
import { DynamicImportModule } from "@modules/dynamic_import/DynamicImportModule";
import { I18nModule } from "@modules/i18n/I18nModule";
import { MoneyModule } from "@modules/money/MoneyModule";
import { injectable } from "tsyringe";

import type { LoggerInterface } from "@interfaces/LoggerInterface";
import type { WidgetInterface } from "@interfaces/WidgetInterface";

import RewardsWaysToEarnPageWidgetComponent from "./RewardsWaysToEarnPageWidget.svelte";
import type { RewardsWaysToEarnPageWidgetConfig } from "./RewardsWaysToEarnPageWidgetConfig";

@injectable()
class RewardsWaysToEarnPageWidget implements WidgetInterface {
    constructor(
        private readonly config: RewardsWaysToEarnPageWidgetConfig,
        private readonly logger: LoggerInterface
    ) {
        this.logger.debug("RewardsWaysToEarnPageWidget is initialized");
    }
    async init(): Promise<this> {
        const widgetNamespace = TRANSLATION_NAMESPACES.waysToEarnPageWidget;
        const pointsNamespace = TRANSLATION_NAMESPACES.commonCriticalPoints;

        const diResolverService = new RewardsDiResolverService();
        const {
            programAccountService,
            earningRulesService,
            boostCampaignService,
            storefrontValuesService,
            boostCampaignQuery,
            customerQuery,
            customerService,
            programAccountQuery,
            currencyFormatter,
            amountFormatter,
            modalManager,
            dynamicImportModule,
            i18nModule,
            commonModule,
            moneyModule,
            authenticationModule,
            localizationInfoProvider,
            customizationManager,
            growaveConfiguration,
        } = diResolverService.resolveAll({
            programAccountService: RewardsProgramAccountService,
            earningRulesService: RewardsEarningRulesService,
            boostCampaignService: RewardsBoostCampaignService,
            customerQuery: CustomerQuery,
            customerService: CustomerService,
            storefrontValuesService: RewardsStorefrontValuesService,
            boostCampaignQuery: RewardsBoostCampaignQuery,
            programAccountQuery: RewardsProgramAccountQuery,
            currencyFormatter: RewardsCurrencyFormatter,
            amountFormatter: RewardsAmountFormatter,
            modalManager: modalManagerToken,
            dynamicImportModule: DynamicImportModule,
            i18nModule: I18nModule,
            commonModule: CommonModule,
            moneyModule: MoneyModule,
            authenticationModule: AuthenticationModule,
            localizationInfoProvider: localizationInfoProviderToken,
            customizationManager: CustomizationManager,
            growaveConfiguration: growaveConfigurationToken,
        });

        const { i18n, i18nApiLoaderFactory, i18nCriticalLoaderFactory } =
            i18nModule.exports;
        i18n.registerLoader(
            widgetNamespace,
            i18nApiLoaderFactory.getLoader(widgetNamespace)
        );
        i18n.registerLoader(
            pointsNamespace,
            i18nCriticalLoaderFactory.getLoader(pointsNamespace)
        );

        const { urlsBuilder, locationController, designModeProvider } =
            commonModule.exports;
        const { htmlSanitizer: sanitizer } = moneyModule.exports;

        const { authenticationInfoProvider } = authenticationModule.exports;

        const element = this.config.target;
        const shadowRoot = isShadowRoot(
            this.config.isWidgetShadowRootEnabled,
            growaveConfiguration.isShadowRootDisabledGlobally
        )
            ? element.shadowRoot || element.attachShadow({ mode: "open" })
            : null;

        const customizationManagerConfig = {
            widgetName: REWARDS_WIDGET_NAMES.REWARDS_WAYS_TO_EARN_PAGE_WIDGET,
            placeholder: element,
            shadowRoot,
        };

        customizationManager.injectStylesFromBlockCustomCss({
            element,
            shadowRoot,
            scriptSelector: DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR,
        });
        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );

        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        new RewardsWaysToEarnPageWidgetComponent({
            target: shadowRoot || element,
            props: {
                programAccountService,
                earningRulesService,
                boostCampaignService,
                storefrontValuesService,
                boostCampaignQuery,
                customerQuery,
                customerService,
                programAccountQuery,
                i18n,
                currencyFormatter,
                amountFormatter,
                modalManager,
                dynamicImportModule,
                urlsBuilder,
                sanitizer,
                locationController,
                authenticationInfoProvider,
                localizationInfoProvider,
                namespace: widgetNamespace,
                pointsNamespace,
                showPointsBalance: this.config.showPointsBalance,
                isDesignMode: designModeProvider.isDesignMode(),
                afterWidgetInject: () =>
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    ),
            },
        });

        return this;
    }
}

export default RewardsWaysToEarnPageWidget;
